import { useState, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Plus, 
  Play, 
  Pause, 
  Trash2, 
  FolderOpen, 
  Settings2, 
  Clock,
  Database,
  GitBranch,
  Bot,
  Shield,
  Zap
} from 'lucide-preact';
import type { ClaudeCodeSession } from '../../shared/types';

interface SessionManagerProps {
  sessions: ClaudeCodeSession[];
  currentSession: ClaudeCodeSession | null;
  onSessionSelect: (session: ClaudeCodeSession) => void;
  onCreateSession: (name: string, githubRepo?: string) => Promise<string | undefined>;
  onLoadSessions: () => void;
  onDeleteSession: (sessionId: string) => Promise<void>;
  onResumeSession: (sessionId: string) => Promise<void>;
}

export function SessionManager({ 
  sessions, 
  currentSession, 
  onSessionSelect, 
  onCreateSession,
  onLoadSessions,
  onDeleteSession,
  onResumeSession
}: SessionManagerProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionRepo, setNewSessionRepo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return;
    
    setIsLoading(true);
    try {
      const sessionId = await onCreateSession(
        newSessionName.trim(), 
        newSessionRepo.trim() || undefined
      );
      
      if (sessionId) {
        setNewSessionName('');
        setNewSessionRepo('');
        setIsCreating(false);
        await onLoadSessions();
      }
    } catch (error) {
      console.error('Failed to create session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (confirm('Are you sure you want to delete this session?')) {
      try {
        await onDeleteSession(sessionId);
        await onLoadSessions();
      } catch (error) {
        console.error('Failed to delete session:', error);
      }
    }
  };

  const handleResumeSession = async (sessionId: string) => {
    try {
      await onResumeSession(sessionId);
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'paused': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Sessions</h2>
          <p className="text-sm text-muted-foreground">
            Manage your Byte DevOps sessions
          </p>
        </div>
        <Button
          onClick={() => setIsCreating(true)}
          size="sm"
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          New Session
        </Button>
      </div>

      {/* Create Session Form */}
      {isCreating && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create New Session</CardTitle>
            <CardDescription>
              Start a new Byte DevOps session
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="session-name">Session Name</Label>
              <Input
                id="session-name"
                value={newSessionName}
                onInput={(e) => setNewSessionName((e.target as HTMLInputElement).value)}
                placeholder="My coding session"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="github-repo">GitHub Repository (Optional)</Label>
              <Input
                id="github-repo"
                value={newSessionRepo}
                onInput={(e) => setNewSessionRepo((e.target as HTMLInputElement).value)}
                placeholder="username/repository"
                disabled={isLoading}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCreateSession}
                disabled={!newSessionName.trim() || isLoading}
                size="sm"
              >
                {isLoading ? 'Creating...' : 'Create Session'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsCreating(false)}
                disabled={isLoading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      <div className="space-y-2">
        {sessions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No sessions yet</h3>
              <p className="text-sm text-muted-foreground text-center mb-4">
                Create your first session to start using Byte DevOps
              </p>
              <Button onClick={() => setIsCreating(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Session
              </Button>
            </CardContent>
          </Card>
        ) : (
          sessions.map((session) => (
            <Card 
              key={session.sessionId}
              className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                currentSession?.sessionId === session.sessionId ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => onSessionSelect(session)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium truncate">{session.name}</h3>
                      <Badge 
                        variant="secondary" 
                        className={`${getStatusColor(session.status)} text-white text-xs`}
                      >
                        {session.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Bot className="h-3 w-3" />
                        <span>{session.model}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3" />
                        <span>{session.permissionMode}</span>
                      </div>
                      
                      {session.githubRepo && (
                        <div className="flex items-center gap-2">
                          <GitBranch className="h-3 w-3" />
                          <span>{session.githubRepo}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        <span>Last active: {formatDate(session.lastActivity)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResumeSession(session.sessionId);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(session.sessionId);
                      }}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Session Stats */}
      {sessions.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Total Sessions:</span>
                <span className="font-medium">{sessions.length}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Active:</span>
                <span className="font-medium">
                  {sessions.filter(s => s.status === 'active').length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}